import { motion, AnimatePresence, useInView } from "framer-motion";
import { useLanguage } from "@/contexts/LanguageContext";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Check, AlertCircle, ArrowUp, ArrowDown } from "lucide-react";
import { useState, useEffect, useRef } from "react";

// Animated number component with scroll trigger
function AnimatedNumber({ 
  value, 
  direction, 
  suffix = "",
  prefix = ""
}: { 
  value: number; 
  direction: "up" | "down" | "neutral"; 
  suffix?: string;
  prefix?: string;
}) {
  const [displayValue, setDisplayValue] = useState(0);
  const ref = useRef<HTMLDivElement>(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  useEffect(() => {
    if (!isInView) return;
    
    const duration = 1500;
    const startTime = Date.now();
    
    const animate = () => {
      const elapsed = Date.now() - startTime;
      const progress = Math.min(elapsed / duration, 1);
      // Easing function for smooth animation
      const easeOut = 1 - Math.pow(1 - progress, 3);
      setDisplayValue(Math.round(value * easeOut));
      
      if (progress < 1) {
        requestAnimationFrame(animate);
      }
    };
    
    requestAnimationFrame(animate);
  }, [isInView, value]);

  return (
    <div ref={ref} className="flex items-center justify-center gap-1">
      {prefix && <span className="font-bold">{prefix}</span>}
      <span className="font-bold">{displayValue}{suffix}</span>
      {direction === "up" && <ArrowUp className="w-5 h-5 text-orange-500" />}
      {direction === "down" && <ArrowDown className="w-5 h-5 text-green-500" />}
    </div>
  );
}

// Parse improvement value to extract number and metadata
function parseImprovementValue(value: string): { 
  number: number; 
  suffix: string; 
  direction: "up" | "down" | "neutral";
  prefix: string;
} {
  const hasUp = value.includes("↑");
  const hasDown = value.includes("↓");
  const direction = hasUp ? "up" : hasDown ? "down" : "neutral";
  
  // Remove arrows
  let cleanValue = value.replace("↑", "").replace("↓", "").trim();
  
  // Extract number and suffix
  const match = cleanValue.match(/([<>]?\s*)(\d+(?:\.\d+)?)(.*)/);
  if (match) {
    return {
      prefix: match[1] || "",
      number: parseFloat(match[2] ?? "0"),
      suffix: match[3] || "",
      direction
    };
  }
  
  return { prefix: "", number: 0, suffix: cleanValue, direction };
}

// Import background images
import bgThailand from "@/assets/solution-bg-thailand.jpg";
import bgChina from "@/assets/solution-bg-china.jpg";
import bgGrowth from "@/assets/solution-bg-growth.jpg";

export function SolutionTabsSection() {
  const { t } = useLanguage();
  const [activeTab, setActiveTab] = useState("thailand");

  const backgroundImages: Record<string, string> = {
    thailand: bgThailand,
    china: bgChina,
    growth: bgGrowth,
  };

  const tabs = [
    {
      id: "thailand",
      label: t("solutions.tab1.label"),
      hero: t("solutions.tab1.hero"),
      heroSub: t("solutions.tab1.heroSub"),
      painPoints: [
        t("solutions.tab1.pain1"),
        t("solutions.tab1.pain2"),
        t("solutions.tab1.pain3"),
      ],
      solutions: [
        t("solutions.tab1.sol1"),
        t("solutions.tab1.sol2"),
        t("solutions.tab1.sol3"),
      ],
      improvements: [
        { value: t("solutions.tab1.impVal1"), label: t("solutions.tab1.imp1") },
        { value: t("solutions.tab1.impVal2"), label: t("solutions.tab1.imp2") },
        { value: t("solutions.tab1.impVal3"), label: t("solutions.tab1.imp3") },
      ],
      whyText: t("solutions.tab1.why"),
    },
    {
      id: "growth",
      label: t("solutions.tab3.label"),
      hero: t("solutions.tab3.hero"),
      heroSub: t("solutions.tab3.heroSub"),
      painPoints: [
        t("solutions.tab3.pain1"),
        t("solutions.tab3.pain2"),
        t("solutions.tab3.pain3"),
      ],
      solutions: [
        t("solutions.tab3.sol1"),
        t("solutions.tab3.sol2"),
        t("solutions.tab3.sol3"),
      ],
      improvements: [
        { value: t("solutions.tab3.impVal1"), label: t("solutions.tab3.imp1") },
        { value: t("solutions.tab3.impVal2"), label: t("solutions.tab3.imp2") },
        { value: t("solutions.tab3.impVal3"), label: t("solutions.tab3.imp3") },
      ],
      whyText: t("solutions.tab3.why"),
    },
    {
      id: "china",
      label: t("solutions.tab2.label"),
      hero: t("solutions.tab2.hero"),
      heroSub: t("solutions.tab2.heroSub"),
      painPoints: [
        t("solutions.tab2.pain1"),
        t("solutions.tab2.pain2"),
        t("solutions.tab2.pain3"),
      ],
      solutions: [
        t("solutions.tab2.sol1"),
        t("solutions.tab2.sol2"),
        t("solutions.tab2.sol3"),
      ],
      improvements: [
        { value: t("solutions.tab2.impVal1"), label: t("solutions.tab2.imp1") },
        { value: t("solutions.tab2.impVal2"), label: t("solutions.tab2.imp2") },
        { value: t("solutions.tab2.impVal3"), label: t("solutions.tab2.imp3") },
      ],
      whyText: t("solutions.tab2.why"),
    },
  ];

  return (
    <section className="relative">
      {/* Background Image Section - only covers title and hero area */}
      <div className="absolute inset-x-0 top-0 h-[500px] overflow-hidden">
        {/* All background images stacked, with smooth opacity transitions */}
        {Object.entries(backgroundImages).map(([tabId, imageSrc]) => (
          <motion.div
            key={tabId}
            initial={false}
            animate={{ opacity: activeTab === tabId ? 1 : 0 }}
            transition={{ duration: 0.6, ease: "easeInOut" }}
            className="absolute inset-0"
          >
            <img
              src={imageSrc}
              alt={
                tabId === "thailand"
                  ? "S.E.Asia cross-border freight forwarding operations background"
                  : tabId === "china"
                    ? "China international freight forwarding logistics background"
                    : "Global freight forwarder growth and digital operations background"
              }
              className="w-full h-full object-cover"
              style={{ objectPosition: tabId === 'growth' ? 'center 50%' : 'center center' }}
            />
          </motion.div>
        ))}
        {/* Dark green overlay with 30% opacity */}
        <div className="absolute inset-0 bg-[hsl(160,50%,15%,0.3)]" />
        {/* Gradient fade to background */}
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-background" />
      </div>

      <div className="relative pt-20 pb-10">
        <div className="container mx-auto px-4">
          {/* Hero */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h1 className="text-3xl md:text-5xl font-bold text-white mb-4">
              {t("solutions.title")}
            </h1>
            <p className="text-lg text-white/80 max-w-2xl mx-auto">
              {t("solutions.subtitle")}
            </p>
          </motion.div>

          {/* Tabs */}
          <Tabs 
            defaultValue="thailand" 
            className="w-full"
            onValueChange={(value) => setActiveTab(value)}
          >
            <TabsList className="grid w-full grid-cols-3 mb-8 h-auto p-1 bg-background/80 backdrop-blur-xs">
              {tabs.map((tab) => (
                <TabsTrigger
                  key={tab.id}
                  value={tab.id}
                  className="text-sm md:text-base py-3 px-4 data-[state=active]:bg-secondary data-[state=active]:text-secondary-foreground"
                >
                  {tab.label}
                </TabsTrigger>
              ))}
            </TabsList>

            {tabs.map((tab) => (
              <TabsContent key={tab.id} value={tab.id}>
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4 }}
                  className="space-y-8"
                >
                  {/* Tab Hero */}
                  <div className="bg-card/80 rounded-2xl p-8 md:p-12 text-center backdrop-blur-xs">
                    <h2 className="text-2xl md:text-3xl font-bold text-foreground mb-3">
                      {tab.hero}
                    </h2>
                    <p className="text-lg text-muted-foreground">{tab.heroSub}</p>
                  </div>

                  {/* Pain Points & Solutions */}
                  <div className="grid md:grid-cols-2 gap-6">
                    {/* Pain Points */}
                    <motion.div 
                      initial={{ opacity: 0, x: -20 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.5, delay: 0.1 }}
                      className="relative bg-gradient-to-br from-card via-card to-destructive/5 rounded-2xl p-6 border border-destructive/20 shadow-lg overflow-hidden group hover:shadow-xl transition-all duration-300"
                    >
                      {/* Decorative background pattern */}
                      <div className="absolute top-0 right-0 w-32 h-32 bg-destructive/5 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2" />
                      <div className="absolute bottom-0 left-0 w-24 h-24 bg-destructive/5 rounded-full blur-2xl translate-y-1/2 -translate-x-1/2" />
                      
                      <div className="relative">
                        <div className="flex items-center gap-4 mb-6">
                          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-destructive/20 to-destructive/10 flex items-center justify-center shadow-inner">
                            <AlertCircle className="w-6 h-6 text-destructive" />
                          </div>
                          <div>
                            <h3 className="text-xl font-bold text-foreground">
                              {t("solutions.painPointsTitle")}
                            </h3>
                            <p className="text-xs text-muted-foreground">{t("solutions.painPointsSubtitle")}</p>
                          </div>
                        </div>
                        
                        <ul className="space-y-4">
                          {tab.painPoints.map((point, i) => (
                            <motion.li 
                              key={i} 
                              initial={{ opacity: 0, x: -10 }}
                              whileInView={{ opacity: 1, x: 0 }}
                              viewport={{ once: true }}
                              transition={{ duration: 0.3, delay: 0.2 + i * 0.1 }}
                              className="flex items-start gap-3 p-3 rounded-lg bg-destructive/5 hover:bg-destructive/10 transition-colors duration-200"
                            >
                              <div className="w-6 h-6 rounded-full bg-destructive/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                                <span className="text-xs font-bold text-destructive">{i + 1}</span>
                              </div>
                              <span className="text-muted-foreground leading-relaxed">{point}</span>
                            </motion.li>
                          ))}
                        </ul>
                      </div>
                    </motion.div>

                    {/* Solutions */}
                    <motion.div 
                      initial={{ opacity: 0, x: 20 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.5, delay: 0.2 }}
                      className="relative bg-gradient-to-br from-card via-card to-secondary/10 rounded-2xl p-6 border-2 border-secondary/30 shadow-lg overflow-hidden group hover:shadow-xl hover:border-secondary/50 transition-all duration-300"
                    >
                      {/* Decorative background pattern */}
                      <div className="absolute top-0 right-0 w-40 h-40 bg-secondary/10 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2" />
                      <div className="absolute bottom-0 left-0 w-32 h-32 bg-secondary/10 rounded-full blur-2xl translate-y-1/2 -translate-x-1/2" />
                      
                      {/* Success badge */}
                      <div className="absolute top-4 right-4 px-3 py-1 rounded-full bg-secondary/20 text-secondary text-xs font-medium">
                        ✨ {t("solutions.solutionsBadge")}
                      </div>
                      
                      <div className="relative">
                        <div className="flex items-center gap-4 mb-6">
                          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-secondary/30 to-secondary/10 flex items-center justify-center shadow-inner">
                            <Check className="w-6 h-6 text-secondary" />
                          </div>
                          <div>
                            <h3 className="text-xl font-bold text-foreground">
                              {t("solutions.solutionsTitle")}
                            </h3>
                            <p className="text-xs text-muted-foreground">{t("solutions.solutionsSubtitle")}</p>
                          </div>
                        </div>
                        
                        <ul className="space-y-4">
                          {tab.solutions.map((solution, i) => (
                            <motion.li 
                              key={i} 
                              initial={{ opacity: 0, x: 10 }}
                              whileInView={{ opacity: 1, x: 0 }}
                              viewport={{ once: true }}
                              transition={{ duration: 0.3, delay: 0.3 + i * 0.1 }}
                              className="flex items-start gap-3 p-3 rounded-lg bg-secondary/5 hover:bg-secondary/10 border border-secondary/10 hover:border-secondary/20 transition-all duration-200"
                            >
                              <div className="w-6 h-6 rounded-full bg-secondary/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                                <Check className="w-4 h-4 text-secondary" />
                              </div>
                              <span className="text-foreground leading-relaxed font-medium">{solution}</span>
                            </motion.li>
                          ))}
                        </ul>
                      </div>
                    </motion.div>
                  </div>

                  {/* Expected Improvements */}
                  <div className="bg-gradient-to-r from-secondary/10 to-secondary/5 rounded-xl p-6">
                    <h3 className="text-lg font-semibold text-foreground mb-6 text-center">
                      {t("solutions.improvementsTitle")}
                    </h3>
                    <div className="grid grid-cols-3 gap-4">
                      {tab.improvements.map((item, i) => {
                        const parsed = parseImprovementValue(item.value);
                        return (
                          <div key={i} className="text-center">
                            <div className={`text-2xl md:text-3xl mb-1 ${
                              parsed.direction === "up" 
                                ? "text-orange-500" 
                                : parsed.direction === "down" 
                                  ? "text-green-500" 
                                  : "text-gradient-ai"
                            }`}>
                              <AnimatedNumber 
                                value={parsed.number} 
                                direction={parsed.direction}
                                suffix={parsed.suffix}
                                prefix={parsed.prefix.trim()}
                              />
                            </div>
                            <div className="text-sm text-muted-foreground">
                              {item.label}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  {/* Why Suitable */}
                  <div className="bg-card rounded-xl p-6 border border-border">
                    <h3 className="text-lg font-semibold text-foreground mb-3">
                      {t("solutions.whySuitable")}
                    </h3>
                    <p className="text-muted-foreground">{tab.whyText}</p>
                  </div>

                  {/* CTA */}
                  <div className="text-center">
                    <Button asChild variant="hero" size="lg">
                      <a
                        href="https://www.deep-insights.ai/book-demo"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {t("solutions.ctaDiagnosis")}
                      </a>
                    </Button>
                  </div>
                </motion.div>
              </TabsContent>
            ))}
          </Tabs>
        </div>
      </div>
    </section>
  );
}
